import React, { useEffect, useCallback } from 'react';
import { debug } from '@/utils/debug';
import { formatAccountId } from '@/utils/googleAds';
import styles from './PollingStates.module.css';
import modalStyles from '@/styles/ModalStyles.module.css';

interface PollingErrorProps {
  customerId: string;
  errorMessage: string;
  onRetry: () => void;
  onGoHome: () => void;
  onViewCampaigns?: () => void; // Optional callback for "View Campaigns" action
  // True when the backend explicitly returned a server-side eligibility
  // rejection (e.g. /complete-setup with success=false from the tier
  // aggregate gate). Drives the softer "Account Not Eligible" framing and
  // suppresses the generic troubleshooting suggestions. Inferring this from
  // keyword-matching errorMessage is unsafe — any 500/unknown error would
  // hit the same catch-all bucket.
  isIneligible?: boolean;
}

export function PollingError({
  customerId,
  errorMessage,
  onRetry,
  onGoHome,
  onViewCampaigns,
  isIneligible = false,
}: PollingErrorProps) {
  debug.log('PollingError: Rendering error state', { customerId, errorMessage });

  const handleRetry = () => {
    debug.log('PollingError: Retry requested');
    onRetry();
  };

  const handleGoHome = () => {
    debug.log('PollingError: Go home requested');
    onGoHome();
  };

  const handleViewCampaigns = () => {
    debug.log('PollingError: View campaigns requested');
    if (onViewCampaigns) {
      onViewCampaigns();
    } else {
      // Fallback to onGoHome if onViewCampaigns not provided
      onGoHome();
    }
  };

  const getErrorType = () => {
    const lowerError = errorMessage.toLowerCase();

    if (lowerError.includes('already linked')) {
      return 'already_linked';
    } else if (lowerError.includes('customer id') || lowerError.includes('invalid')) {
      return 'invalid_customer_id';
    } else if (lowerError.includes('permission') || lowerError.includes('access')) {
      return 'permission_error';
    } else if (lowerError.includes('network') || lowerError.includes('connection')) {
      return 'network_error';
    } else if (lowerError.includes('timeout')) {
      return 'timeout_error';
    } else if (lowerError.includes('setup') || lowerError.includes('configuration')) {
      return 'setup_error';
    } else {
      return 'unknown_error';
    }
  };

  const getErrorHelp = () => {
    const errorType = getErrorType();

    switch (errorType) {
      case 'already_linked':
        return {
          title: 'Unable to Link Account',
          suggestions: [
            'Please try a different Google Ads account',
            'Or contact support@flouri.ai for assistance'
          ]
        };

      case 'invalid_customer_id':
        return {
          title: 'Invalid Customer ID',
          suggestions: [
            'Double-check your Google Ads Customer ID format (XXX-XXX-XXXX)',
            'Ensure you\'re using the correct 10-digit Customer ID',
            'Verify the Customer ID from your Google Ads account settings',
            'Make sure you have access to this Google Ads account'
          ]
        };
      
      case 'permission_error':
        return {
          title: 'Permission Issue',
          suggestions: [
            'Ensure you have admin access to the Google Ads account',
            'Check that your Google account has the necessary permissions',
            'Verify you\'re signed in to the correct Google account',
            'Contact your Google Ads account administrator for access'
          ]
        };
      
      case 'network_error':
        return {
          title: 'Connection Problem',
          suggestions: [
            'Check your internet connection',
            'Try again in a few moments',
            'Disable VPN if you\'re using one',
            'Contact support if the issue persists'
          ]
        };

      case 'timeout_error':
        return {
          title: 'Request Timed Out',
          suggestions: [
            'The server took too long to respond',
            'This is usually temporary - please try again',
            'Check your internet connection stability',
            'If the problem persists, try again in a few minutes'
          ]
        };

      case 'setup_error':
        return {
          title: 'Setup Configuration Error',
          suggestions: [
            'The account linking process encountered an issue',
            'Try the linking process again from the beginning',
            'Ensure your Google Ads account is properly configured',
            'Contact support for assistance with account setup'
          ]
        };
      
      default:
        return {
          title: 'Unexpected Error',
          suggestions: [
            'Try the linking process again',
            'Check your Google Ads account status',
            'Ensure you have a stable internet connection',
            'Contact support if the problem continues'
          ]
        };
    }
  };

  const errorHelp = getErrorHelp();
  // For server-side eligibility rejections (caller passed isIneligible=true),
  // surface the server's full message as the primary text and suppress the
  // bucketed troubleshooting suggestions — the message itself contains the
  // reason and the recommended action. For all other errors (timeouts,
  // network failures, unrecognized 5xx bodies, etc.) keep the generic
  // "We encountered an issue..." framing with the bucketed help.
  const hasServerMessage = Boolean(errorMessage && errorMessage.trim());
  const showGenericSuggestions = !isIneligible;
  // "Connection Failed" implies a technical failure. For ineligibility the
  // link attempt itself worked; the account just wasn't eligible.
  const modalTitle = isIneligible ? 'Account Not Eligible' : 'Connection Failed';

  // Handle Escape key to close modal
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onGoHome();
    }
  }, [onGoHome]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const modalTitleId = 'polling-error-title';

  return (
    <div
      className={modalStyles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby={modalTitleId}
    >
      <div className={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={modalStyles.modalHeader}>
          <h3 id={modalTitleId} className={modalStyles.modalTitle}>{modalTitle}</h3>
          <button
            className={modalStyles.modalClose}
            onClick={onGoHome}
            aria-label="Close dialog"
          >
            &times;
          </button>
        </div>

        <div className={modalStyles.modalBody}>
          <div className={styles.container}>
            <div className={styles.header}>
              <div className={`${styles.icon} ${styles.errorIcon}`}>❌</div>
            </div>

            <div className={styles.content}>
        <div className={styles.message}>
          <p className={styles.primaryText}>
            {isIneligible && hasServerMessage
              ? errorMessage
              : 'We encountered an issue while connecting your Google Ads account.'}
          </p>
          <p className={styles.secondaryText}>
            <strong>Customer ID:</strong> {formatAccountId(customerId)}
          </p>
        </div>

        {showGenericSuggestions && (
          <div className={`${styles.infoSection} ${styles.helpSection}`}>
            <h4 className={styles.sectionTitle}>{errorHelp.title}</h4>
            <ul className={styles.stepsList}>
              {errorHelp.suggestions.map((suggestion, index) => (
                <li key={index}>{suggestion}</li>
              ))}
            </ul>
          </div>
        )}

        <div className={styles.actions}>
          <button 
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={handleRetry}
          >
            Try Again
          </button>
          
          {errorMessage.toLowerCase().includes('timeout') && errorMessage.includes('linked successfully') ? (
            <button
              className={`${styles.btn} ${styles.btnSecondary}`}
              onClick={handleViewCampaigns}
            >
              View Campaigns
            </button>
          ) : (
            <button 
              className={`${styles.btn} ${styles.btnSecondary}`}
              onClick={handleGoHome}
            >
              Back to Dashboard
            </button>
          )}
        </div>

        <div className={`${styles.help} ${styles.errorHelp}`}>
          <p className={styles.helpText}>
            <strong>Need more help?</strong> If you continue to experience issues, please contact our support team
            with the error details above. We're here to help get your Google Ads account connected.
          </p>
        </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}